import { cloneDeep } from "lodash"
import {
  CHECKOUT,
  EDIT_CART,
  ENTER_ADDRESS_MANUALLY,
  LOAD_MORE,
  NAVIGATION,
  ORDER_SUMMARY,
  PRODUCT_NAVIGATION,
  PROMO,
  SHIPPING,
  SAVE_AND_CONTINUE,
  CONTINUE,
  MORE_DETAILS,
  DELIVERY_OPTIONS,
  LANDING,
  CALL_SUPPORT_PHONE_NUMBER,
  HELP_SUPPORT,
  CUSTOMER_SUPPORT,
  PAYMENT_BILLING,
  CREDIT_CARD,
  REVIEW,
  PAYPAL,
  PAYMENTS,
  CHECKOUT_WITH_PAYPAL,
  CHECKOUT_WITH,
  CHECKOUT_PROCESS_BUTTON,
  CHECKOUT_PCIPAL,
  CREATE_A_QUICK_ACCOUNT,
  FORGOT_PASSWORD,
  PASSWORD_RESET,
  HOMEOWNER_ACCOUNT,
  PROFESSIONAL_ACCOUNT,
  SAVE_CHANGES,
  SHIPPING_VERIFY_ADDRESS,
  BILLING_INFORMATION,
  PLACE_ORDER,
  PAYMENT_METHOD,
  SIGN_IN,
  CITY_DROPDOWN,
  PROFESSIONAL,
  SELECT_BUSINESS_TYPE,
  SAVE_ADDRESS_TO_MY_ACCOUNT,
  CHECKBOX,
  SIGN_ME_UP_FOR_THE_E_NEWSLETTER,
  MOUSE_OVER,
  SHIPPING_RESTRICTIONS,
  SUBMIT,
  RESEND_EMAIL,
  PAYPAL_ERROR,
  NOT_APPLICABLE,
} from "@/components/checkout/v2/analytics/constants"
import { findSelectParcelType } from "../DeliveryOptions/utils"
import {
  calculateCartData,
  getAnalyticsUrl,
  sanitizeTextForAnalytics,
  getUserAnalyticsObject,
  shaEncrypt,
} from "@/utils/helper"
import { store } from "@/store"

const getAnalyticsCartData = async cartObj => {
  if (
    !cartObj ||
    typeof cartObj !== "object" ||
    Object.keys(cartObj).length === 0
  ) {
    return null
  }

  try {
    return await calculateCartData({ cart: cartObj }, true)
  } catch (error) {
    console.warn("Cart calculation failed:", error)
    return null
  }
}

export const enterAddressManuallyAnalytics = () => {
  const eventInfo = getEventInfo(ENTER_ADDRESS_MANUALLY)
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addEmailAnalytics = async data => {
  const {
    isAuth,
    email,
    errors,
    type,
    saveChanges,
    isCreateAccount = false,
    isVerifyAddress = false,
    selectedAccount,
    newsletterSignup = false,
  } = data
  const internalLinkName =
    type === "edit" ? saveChanges.toLowerCase() : SAVE_AND_CONTINUE

  let eventAction
  let internalLinkType
  let internalLinkZoneName
  if (isCreateAccount) {
    eventAction = `${CHECKOUT}:${SHIPPING}:${
      selectedAccount === "homeowner" ? HOMEOWNER_ACCOUNT : PROFESSIONAL_ACCOUNT
    }:${SAVE_AND_CONTINUE}`

    internalLinkType = `${CHECKOUT}:${
      selectedAccount === "homeowner" ? HOMEOWNER_ACCOUNT : PROFESSIONAL_ACCOUNT
    }`

    internalLinkZoneName = `${CHECKOUT}:${SHIPPING}:${CREATE_A_QUICK_ACCOUNT}`
  } else if (isVerifyAddress) {
    eventAction = `${CHECKOUT}:${SHIPPING}:${SAVE_CHANGES}`
    internalLinkType = `${CHECKOUT}:${NAVIGATION}`
    internalLinkZoneName = `${CHECKOUT}:${SHIPPING}`
  } else {
    eventAction = `${CHECKOUT}:${SHIPPING}:${
      type === "edit" ? SAVE_CHANGES : SAVE_AND_CONTINUE
    }`

    internalLinkType = `${CHECKOUT}:${NAVIGATION}`

    internalLinkZoneName = `${CHECKOUT}:${SHIPPING}`
  }

  const eventInfo = {
    eventAction,
    eventName: eventAction,
    eventType: NAVIGATION,
    eventMsg: !errors.length ? "n/a" : errors.join("|")?.toLowerCase(),
    eventStatus: !errors.length ? "success" : "failure",
    internalLinkName,
    internalLinkPosition: CHECKOUT,
    internalLinkType,
    internalLinkZoneName,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
    ...(isCreateAccount &&
      !errors.length && { newsletterSignUp: newsletterSignup }),
  }

  const { adobeDataLayer: { getState } = {} } = window
  const page = (getState && getState("page")) || {}
  const cartObj = store?.getState()?.cart?.cart ?? {}
  const cartData = await getAnalyticsCartData(cartObj)
  page.cart = cartData?.cart || {}

  delete page.pageName
  delete page.pageUrl
  delete page.productInfo

  if (!isAuth && email) {
    shaEncrypt(email)
      .then(encryptedEmail => {
        localStorage.setItem("hashedEmail", encryptedEmail)
        page.user = getUserAnalyticsObject({ encryptedEmail })
        window.adobeDataLayer.push({
          event: "cmp:click",
          page: page,
          eventInfo,
        })
      })
      .catch(er => {
        window.adobeDataLayer.push({
          event: "cmp:click",
          page: page,
          eventInfo,
        })
      })
  } else {
    window.adobeDataLayer.push({
      event: "cmp:click",
      page: page,
      eventInfo,
    })
  }
}

export const createQuickAccountAnalytics = () => {
  const eventInfo = getEventInfo(CREATE_A_QUICK_ACCOUNT)
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const forgotPasswordAnalytics = () => {
  const eventInfo = getEventInfo(FORGOT_PASSWORD)
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const forgotPasswordModalAnalytics = ({
  status,
  isMailSent,
  message,
  i18n,
}) => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${PASSWORD_RESET}:${
      !isMailSent ? SUBMIT : RESEND_EMAIL
    }`,
    eventName: `${CHECKOUT}:${PASSWORD_RESET}:${
      !isMailSent ? SUBMIT : RESEND_EMAIL
    }`,
    eventType: NAVIGATION,
    eventMsg:
      message ||
      (status === "failure"
        ? i18n.generalAuthenticationError?.toLowerCase()
        : i18n.mailSent?.toLowerCase()),
    eventStatus: status,
    internalLinkName: !isMailSent ? i18n.submit?.toLowerCase() : RESEND_EMAIL,
    internalLinkPosition: "auth modal",
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${PASSWORD_RESET}:form`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }

  pushAnalyticsObjToDataLayer(eventInfo)
}

export const passwordResetCloseAnalytics = () => {
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${PASSWORD_RESET}:close`,
    eventMsg: "n/a",
    eventStatus: "n/a",
    eventName: `${CHECKOUT}:${PASSWORD_RESET}:close`,
    eventType: NAVIGATION,
    internalLinkName: "close",
    internalLinkPosition: "auth modal",
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${PASSWORD_RESET}:form`,
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addLoginAnalytics = (status, errorsList) => {
  if (!window.adobeDataLayer) return

  let formattedErrorsList = errorsList
  if (status === "failure") {
    formattedErrorsList = errorsList.join("|")?.toLowerCase() || "n/a"
  }

  const eventInfo = {
    eventAction: "checkout:shipping:sign in",
    eventName: "checkout:shipping:sign in",
    eventType: "sign in",
    eventMsg: status === "failure" ? formattedErrorsList : "n/a",
    eventStatus: status,
    internalLinkName: "sign in",
    internalLinkPosition: "checkout",
    internalLinkType: "checkout:navigation",
    internalLinkZoneName: "checkout:shipping",
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }

  const userObj = getUserAnalyticsObject()
  pushAnalyticsObjToDataLayer(eventInfo, null, null, null, userObj)
}

const getEventInfo = action => {
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${SHIPPING}:${action}`,
    eventMsg: "n/a",
    eventStatus: "n/a",
    eventName: `${CHECKOUT}:${SHIPPING}:${action}`,
    eventType: NAVIGATION,
    internalLinkName: action,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${SHIPPING}`,
  }

  return eventInfo
}

export const addContinueToPaymentAnalytics = async ({
  selected = {},
  parcels = [],
  isXpp = false,
}) => {
  const parcelTypeName = findSelectParcelType(selected, parcels)

  const { cart: { cart: cartObj = {} } = {} } = store?.getState() ?? {}
  const cartData = await getAnalyticsCartData(cartObj)

  const buttonType = isXpp ? CONTINUE : SAVE_AND_CONTINUE

  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${DELIVERY_OPTIONS}:${buttonType}`,
    eventMsg: "n/a",
    eventName: `${CHECKOUT}:${DELIVERY_OPTIONS}:${buttonType}`,
    eventStatus: "n/a",
    eventType: "navigation",
    internalLinkName: buttonType,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${DELIVERY_OPTIONS}:${parcelTypeName}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${DELIVERY_OPTIONS}`,
  }
  pushAnalyticsObjToDataLayer(eventInfo, null, cartData)
}

export const editCartAnalytics = cartPage => {
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${ORDER_SUMMARY}:${EDIT_CART}`,
    eventMsg: "n/a",
    eventStatus: "n/a",
    eventName: `${CHECKOUT}:${ORDER_SUMMARY}:${EDIT_CART}`,
    eventType: NAVIGATION,
    internalLinkName: EDIT_CART,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkURL: cartPage,
    internalLinkZoneName: `${CHECKOUT}:${LANDING}:${ORDER_SUMMARY}`,
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const loadMoreAnalytics = () => {
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${ORDER_SUMMARY}:${LOAD_MORE}`,
    eventMsg: "n/a",
    eventStatus: "n/a",
    eventName: `${CHECKOUT}:${ORDER_SUMMARY}:${LOAD_MORE}`,
    eventType: NAVIGATION,
    internalLinkName: LOAD_MORE,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${LANDING}:${ORDER_SUMMARY}`,
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const productClickAnalytics = (pdpUrl, productName, productInfo) => {
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${ORDER_SUMMARY}:${PRODUCT_NAVIGATION}`,
    eventMsg: "n/a",
    eventStatus: "n/a",
    eventName: `${CHECKOUT}:${ORDER_SUMMARY}:${PRODUCT_NAVIGATION}`,
    eventType: NAVIGATION,
    internalLinkName: sanitizeTextForAnalytics(productName) || "n/a",
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${PRODUCT_NAVIGATION}`,
    internalLinkURL: getAnalyticsUrl(pdpUrl),
    internalLinkZoneName: `${CHECKOUT}:${ORDER_SUMMARY}`,
  }
  pushAnalyticsObjToDataLayer(eventInfo, productInfo)
}

export const updatePromoAnalytics = async (status, cart, promo, action) => {
  const cartData = await getAnalyticsCartData(cart)
  const eventInfo = {
    eventAction: `${CHECKOUT}:${LANDING}:${action} ${PROMO}`,
    eventName: `${CHECKOUT}:${LANDING}:${action} ${PROMO}`,
    eventType: NAVIGATION,
    eventMsg:
      status === "success"
        ? action === "apply"
          ? "promocode applied successfully"
          : "promocode removed successfully"
        : status?.toLowerCase(),
    eventStatus: status === "success" ? "success" : "failure",
    internalLinkName: action,
    internalLinkPosition: `${CHECKOUT}`,
    internalLinkType: `${CHECKOUT}:${PROMO}`,
    internalLinkZoneName: `${CHECKOUT}:${LANDING}:${ORDER_SUMMARY}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
    promoCode: promo,
  }
  pushAnalyticsObjToDataLayer(eventInfo, null, cartData)
}

export const addAnalyticsForCardPayment = () => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${PAYMENT_BILLING}:checkout with card`,
    eventName: `${CHECKOUT}:${PAYMENT_BILLING}:checkout with card`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: CREDIT_CARD,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${PAYMENT_BILLING}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsForErrorMessages = (
  eventMsg = "n/a",
  eventStatus = "n/a",
  isModalOpen = false,
  eventText = "place order",
  modalCloseText = ""
) => {
  const extraText = isModalOpen ? ":modal:something went wrong" : ""
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${REVIEW}${extraText}${modalCloseText}:${eventText}`,
    eventMsg,
    eventName: `${CHECKOUT}:${REVIEW}${extraText}${modalCloseText}:${eventText}`,
    eventStatus,
    eventType: NAVIGATION,
    internalLinkName: `${eventText}`,
    internalLinkPosition: `${CHECKOUT}${extraText}`,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${REVIEW}${extraText}`,
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsObjForMoreDetails = async () => {
  const { cart: { cart: cartObj = {} } = {} } = store?.getState() ?? {}
  const cartData = await getAnalyticsCartData(cartObj)

  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${DELIVERY_OPTIONS}:${MORE_DETAILS}`,
    eventMsg: "n/a",
    eventName: `${CHECKOUT}:${DELIVERY_OPTIONS}:${MORE_DETAILS}`,
    eventStatus: "n/a",
    eventType: "navigation",
    internalLinkName: MORE_DETAILS,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${DELIVERY_OPTIONS}`,
  }
  pushAnalyticsObjToDataLayer(eventInfo, null, cartData)
}

export const addAnalyticsForErrorCheckout = (
  event,
  customerCareNum = "n/a"
) => {
  event.preventDefault()
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${REVIEW}:${customerCareNum}`,
    eventMsg: "n/a",
    eventName: `${CHECKOUT}:${REVIEW}:${customerCareNum}`,
    eventStatus: "n/a",
    eventType: NAVIGATION,
    internalLinkName: customerCareNum,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkURL: (window.location.href = event.target.href),
    internalLinkZoneName: `${CHECKOUT}:${REVIEW}`,
  }

  pushAnalyticsObjToDataLayer(eventInfo)
}

const pushAnalyticsObjToDataLayer = (
  eventInfo = {},
  productInfo = {},
  cartObj = {},
  paymentMethodName = "",
  userObj = {}
) => {
  const page = cloneDeep(window.pageObj || {})

  delete page.pageName
  delete page.pageUrl
  delete page.productInfo

  if (page?.pageInfo) {
    delete page?.pageInfo?.contentIdentificationCategory
    delete page?.pageInfo?.contentIdentificationRoom
    delete page?.pageInfo?.contentIdentificationPurpose
    delete page?.pageInfo?.contentIdentificationCollection
    delete page?.pageInfo?.contentIdentificationDesignStyle
    delete page?.pageInfo?.contentIdentificationContentType

    page.category.room = NOT_APPLICABLE
    page.category.subCategoryName = CHECKOUT

    if (!page?.pageInfo?.referrer) {
      page.pageInfo.referrer =
        document.referrer || sessionStorage.getItem("previousPageUrl") || "n/a"
      sessionStorage.removeItem("previousPageUrl")
    }
  }

  if (cartObj?.cart) {
    page.cart = cartObj.cart
    const cartItems = page?.cart?.item ?? []
    const cartItemStr = cartItems
      .map(item => item.productInfo?.productID ?? "")
      .filter(Boolean)
      .join(",")
    page.cartInfo = { cartItems: cartItemStr }
    window.pageObj.cart = cartObj.cart
    window.pageObj.cartInfo = { cartItems: cartItemStr }
  }

  if (paymentMethodName) {
    page.cart = {
      ...page.cart,
      payment: { paymentMethod: paymentMethodName },
    }
  }

  if (
    userObj &&
    typeof userObj === "object" &&
    Object.keys(userObj).length > 0
  ) {
    page.user = userObj
  }

  const eventData = {
    event: "cmp:click",
    eventInfo,
    page,
  }

  if (
    productInfo &&
    typeof productInfo === "object" &&
    Object.keys(productInfo).length > 0
  ) {
    eventData.productInfo = productInfo
  }

  window.adobeDataLayer.push(eventData)
}

export const addAnalyticsCallSupportNumber = e => {
  if (!e?.target?.innerText) return
  const innerText = e.target.innerText
  let eventInfo = {}
  const isEmail = innerText.toLowerCase().includes("email")
  const actionValue = isEmail
    ? innerText.toLowerCase()
    : CALL_SUPPORT_PHONE_NUMBER
  eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${LANDING}:${actionValue}`,
    eventMsg: "n/a",
    eventName: `${CHECKOUT}:${LANDING}:${actionValue}`,
    eventStatus: "n/a",
    eventType: `${HELP_SUPPORT}`,
    internalLinkName: isEmail ? innerText.toLowerCase() : innerText,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:navigation`,
    internalLinkURL: isEmail ? e?.target?.href : `tel:${innerText}`,
    internalLinkZoneName: `${CHECKOUT}:${LANDING}:${CUSTOMER_SUPPORT}`,
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const mouseOverTooltipAnalytics = (data = {}) => {
  const { type, i18n } = data
  const eventText = `cart:landing page:${
    type === i18n?.oversizedFragileDelivery
      ? type.toLowerCase().replace("/", " ")
      : `estimated ${type}`
  } mouseover`

  const eventLinkName =
    type === i18n?.oversizedFragileDelivery
      ? sanitizeTextForAnalytics(i18n?.oversizedDelivery)
          ?.split("  ")
          ?.join(" ")
      : i18n?.shippingTooltip.toLowerCase()

  const eventInfo = {
    eventAction: eventText,
    eventName: eventText,
    eventType: "navigation",
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName:
      type === "tax" ? i18n?.taxTooltip.toLowerCase() : eventLinkName,
    internalLinkPosition: "cart",
    internalLinkType: "cart:mouseover",
    internalLinkZoneName: "cart:landing page:order summary",
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }

  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsForPaypal = () => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${PAYMENT_BILLING}:${CHECKOUT_WITH_PAYPAL}`,
    eventName: `${CHECKOUT}:${PAYMENT_BILLING}:${CHECKOUT_WITH_PAYPAL}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: PAYPAL,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${PAYMENT_BILLING}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsForPaypalButton = () => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${PAYMENT_BILLING}:${PAYPAL} ${PAYMENTS}`,
    eventName: `${CHECKOUT}:${PAYMENT_BILLING}:${PAYPAL} ${PAYMENTS}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: PAYPAL,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${PAYMENT_BILLING}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "false",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsForPciPal = (pciPalTxt = "") => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${PAYMENT_BILLING}:${CHECKOUT_WITH} ${pciPalTxt}`,
    eventName: `${CHECKOUT}:${PAYMENT_BILLING}:${CHECKOUT_WITH} ${pciPalTxt}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: CHECKOUT_PCIPAL,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${PAYMENT_BILLING}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsForPciProcessBtnClick = () => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${PAYMENT_BILLING}:${CHECKOUT_WITH} ${CHECKOUT_PCIPAL}:${CHECKOUT_PROCESS_BUTTON}`,
    eventName: `${CHECKOUT}:${PAYMENT_BILLING}:${CHECKOUT_WITH} ${CHECKOUT_PCIPAL}:${CHECKOUT_PROCESS_BUTTON}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "success",
    internalLinkName: CHECKOUT_PCIPAL,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${PAYMENT_BILLING}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsVerifyAddress = (action = "") => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${SHIPPING_VERIFY_ADDRESS}:${action}`,
    eventName: `${CHECKOUT}:${SHIPPING_VERIFY_ADDRESS}:${action}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: action,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${SHIPPING_VERIFY_ADDRESS}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsBillingInformationBtnClick = (btnTxt = "") => {
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${BILLING_INFORMATION}:${btnTxt}`,
    eventMsg: "n/a",
    eventStatus: "success",
    eventName: `${CHECKOUT}:${BILLING_INFORMATION}:${btnTxt}`,
    eventType: NAVIGATION,
    internalLinkName: btnTxt,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${BILLING_INFORMATION}`,
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addPlaceOrderCCAnalytics = (
  paymentMethod = "n/a",
  status = "n/a",
  link,
  errorMsg = ""
) => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${PAYMENT_BILLING}:${PLACE_ORDER}`,
    eventName: `${CHECKOUT}:${PAYMENT_BILLING}:${PLACE_ORDER}`,
    eventType: NAVIGATION,
    eventMsg: errorMsg || "n/a",
    eventStatus: status || "n/a",
    internalLinkName: PLACE_ORDER,
    internalLinkPosition: `${CHECKOUT}`,
    internalLinkType: `${CHECKOUT}:${PAYMENT_METHOD}:${paymentMethod}`,
    internalLinkZoneName: `${CHECKOUT}:${PAYMENT_BILLING}`,
    internalLinkURL: link
      ? (link.indexOf("http") === -1 ? window.location.origin : "") + link
      : "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo, null, null, "Credit Card")
}

export const placeOrderClickFailed = errorMsg => {
  const { checkout: { paymentMethodName = "" } = {} } = store?.getState() ?? {}

  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${REVIEW}:${PLACE_ORDER}`,
    eventMsg: errorMsg || "placeorder failed",
    eventName: `${CHECKOUT}:${REVIEW}:${PLACE_ORDER}`,
    eventStatus: "failure",
    eventType: NAVIGATION,
    internalLinkName: PLACE_ORDER,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${REVIEW}`,
  }
  pushAnalyticsObjToDataLayer(eventInfo, null, null, paymentMethodName)
}

export const addAnalyticsOrderSummaryDropDown = (actionType = "") => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${LANDING}:${ORDER_SUMMARY} ${actionType}`,
    eventName: `${CHECKOUT}:${LANDING}:${ORDER_SUMMARY} ${actionType}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: actionType,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${ORDER_SUMMARY}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addCheckoutSignInAnalyticsData = () => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${SHIPPING}:${SIGN_IN}`,
    eventName: `${CHECKOUT}:${SHIPPING}:${SIGN_IN}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: SIGN_IN,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${SHIPPING}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const paypalXPPErrorOnLanding = async data => {
  const { customErrorMsg } = data
  const { cart: { cart: cartObj = {} } = {} } = store?.getState() ?? {}
  const cartData = await getAnalyticsCartData(cartObj)

  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:landing page:${PAYPAL_ERROR}`,
    eventMsg: customErrorMsg,
    eventName: `${CHECKOUT}:landing page:${PAYPAL_ERROR}`,
    eventStatus: "failure",
    eventType: NAVIGATION,
    internalLinkName: PAYPAL_ERROR,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${SHIPPING}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:landing page`,
  }
  pushAnalyticsObjToDataLayer(eventInfo, null, cartData)
}

export const shippingErrorTaxCalculate = async data => {
  const { customErrorMsg } = data
  const { cart: { cart: cartObj = {} } = {} } = store?.getState() ?? {}
  const cartData = await getAnalyticsCartData(cartObj)

  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${SHIPPING}:${SAVE_AND_CONTINUE}`,
    eventMsg: customErrorMsg,
    eventName: `${CHECKOUT}:${SHIPPING}:${SAVE_AND_CONTINUE}`,
    eventStatus: "failure",
    eventType: NAVIGATION,
    internalLinkName: SAVE_AND_CONTINUE,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${SHIPPING}`,
  }
  pushAnalyticsObjToDataLayer(eventInfo, null, cartData)
}

export const addShippingAnalytics = (status, message, i18n) => {
  if (window.adobeDataLayer) {
    const eventInfo = getShippingEventInfo(SAVE_AND_CONTINUE, true)
    if (status) {
      eventInfo.eventStatus = status
    }
    if (message) {
      if (
        message === "failed to set shipping address." ||
        message === "failed to get shipping options."
      ) {
        message = i18n.shippingProcessError?.toLowerCase()
      }
      eventInfo.eventMsg = message
    }
    pushAnalyticsObjToDataLayer(eventInfo)
  }
}

const getShippingEventInfo = (type, needJSON = false) => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${SHIPPING}:${type}`,
    eventName: `${CHECKOUT}:${SHIPPING}:${type}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: SAVE_AND_CONTINUE,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${SHIPPING}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  if (needJSON) {
    return eventInfo
  }
  return JSON.stringify(eventInfo)
}

export const addAnalyticsDeliveryComboSku = ({
  title = "",
  actionTitle = "",
  parcels = [],
  selected = {},
}) => {
  const parcelTypeName = findSelectParcelType(selected, parcels)
  const eventInfo = {
    eventAction: `${CHECKOUT}:${DELIVERY_OPTIONS}: ${title}${actionTitle}`,
    eventName: `${CHECKOUT}:${DELIVERY_OPTIONS}: ${title}${actionTitle}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: actionTitle,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${parcelTypeName} ${SHIPPING}`,
    internalLinkZoneName: `${CHECKOUT}:${DELIVERY_OPTIONS}: ${title}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsBackOrderTooltip = ({
  title = "",
  parcels = [],
  selected = {},
  tooltipMsg = "",
}) => {
  const parcelTypeName = findSelectParcelType(selected, parcels)
  const eventInfo = {
    eventAction: `${CHECKOUT}:${DELIVERY_OPTIONS}: ${title}:${MOUSE_OVER}`,
    eventName: `${CHECKOUT}:${DELIVERY_OPTIONS}: ${title}:${MOUSE_OVER}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: tooltipMsg,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${parcelTypeName} ${SHIPPING}`,
    internalLinkZoneName: `${CHECKOUT}:${DELIVERY_OPTIONS}: ${title}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsShowHidePasswordClick = ({ btnClickAction = "" }) => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${SHIPPING}: ${SIGN_IN}:${btnClickAction}`,
    eventName: `${CHECKOUT}:${SHIPPING}: ${SIGN_IN}:${btnClickAction}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: btnClickAction,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}: ${btnClickAction}`,
    internalLinkZoneName: `${CHECKOUT}:${SHIPPING}: ${SIGN_IN}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsCityClick = (cityName = "", selectedAccount = "") => {
  const selectedAccountType = selectedAccount
    ? `:${CREATE_A_QUICK_ACCOUNT}:${selectedAccount} account`
    : `:${SHIPPING}`
  const eventInfo = {
    eventAction: `${CHECKOUT}:${SHIPPING}: ${CITY_DROPDOWN}:${cityName}`,
    eventName: `${CHECKOUT}:${SHIPPING}: ${CITY_DROPDOWN}:${cityName}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: cityName,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}${selectedAccountType}`,
    internalLinkZoneName: `${CHECKOUT}:${SHIPPING}: ${CITY_DROPDOWN}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsBusinessTypeDropDownClick = (businessType = "") => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${CREATE_A_QUICK_ACCOUNT}: ${PROFESSIONAL}:${businessType}`,
    eventName: `${CHECKOUT}:${CREATE_A_QUICK_ACCOUNT}: ${PROFESSIONAL}:${businessType}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: businessType,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${SELECT_BUSINESS_TYPE}:${businessType}`,
    internalLinkZoneName: `${CHECKOUT}:${CREATE_A_QUICK_ACCOUNT}: ${PROFESSIONAL}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsSaveAddressClick = (checkboxAction = "") => {
  const selectedText = checkboxAction ? "selection" : "unselection"
  const eventInfo = {
    eventAction: `${CHECKOUT}:${SHIPPING}: ${SAVE_ADDRESS_TO_MY_ACCOUNT}:${CHECKBOX} ${selectedText}`,
    eventName: `${CHECKOUT}:${SHIPPING}: ${SAVE_ADDRESS_TO_MY_ACCOUNT}:${CHECKBOX} ${selectedText}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: `${SAVE_ADDRESS_TO_MY_ACCOUNT}:${CHECKBOX} ${selectedText}`,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${CHECKBOX} ${selectedText}`,
    internalLinkZoneName: `${CHECKOUT}:${SHIPPING}: ${SAVE_ADDRESS_TO_MY_ACCOUNT}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsNewsletterSignupClick = (checkboxAction = "") => {
  const selectedText = checkboxAction ? "selection" : "unselection"
  const eventInfo = {
    eventAction: `${CHECKOUT}:${SHIPPING}: ${SIGN_ME_UP_FOR_THE_E_NEWSLETTER}:${CHECKBOX} ${selectedText}`,
    eventName: `${CHECKOUT}:${SHIPPING}: ${SIGN_ME_UP_FOR_THE_E_NEWSLETTER}:${CHECKBOX} ${selectedText}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: `${SIGN_ME_UP_FOR_THE_E_NEWSLETTER}:${CHECKBOX} ${selectedText}`,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${CHECKBOX} ${selectedText}`,
    internalLinkZoneName: `${CHECKOUT}:${SHIPPING}: ${SIGN_ME_UP_FOR_THE_E_NEWSLETTER}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsRemoveItems = async (product = []) => {
  const cartObj = store?.getState()?.cart?.cart
  const cartData = await getAnalyticsCartData(cartObj)
  const productArrayLists = product ? product.length > 1 : false
  const prodcutLists = []
  if (product?.length) {
    product?.forEach(prod => {
      const { customProductInfo = "{}" } = prod
      const productInformation = JSON.parse(customProductInfo)
      prodcutLists.push(productInformation?.productInfo)
    })
  }
  const removeText = productArrayLists ? "remove items" : "remove item"
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${SHIPPING}:${SAVE_AND_CONTINUE}:${SHIPPING_RESTRICTIONS}:${removeText}`,
    eventMsg: "n/a",
    eventStatus: "n/a",
    eventName: `${CHECKOUT}:${SHIPPING}:${SAVE_AND_CONTINUE}:${SHIPPING_RESTRICTIONS}:${removeText}`,
    eventType: NAVIGATION,
    internalLinkName: removeText,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${SHIPPING_RESTRICTIONS}`,
  }

  const productInfoJSON =
    prodcutLists?.length > 0 ? { productInfo: prodcutLists } : null
  pushAnalyticsObjToDataLayer(eventInfo, productInfoJSON, cartData)
}
